import { useEffect, useState } from "react";
import {
    Box,
    Button,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Switch,
    IconButton,
    Menu,
    MenuItem,
    Typography,
    TableContainer,
    Paper,
    TextField,
    FormGroup,
    Checkbox,
    Select
} from "@mui/material";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import ArrowBackIosIcon from "@mui/icons-material/ArrowBackIos";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import DeleteIcon from '@mui/icons-material/Delete';
import axios from "axios";
import { toggleLoader } from "../../store/loader/loaderReducer";
import { toast } from "react-toastify";
import { useDispatch } from "react-redux";
import { useSelector } from "react-redux";
import styles from './WebhooksPage.module.css';
import AppHeader from '../../component/appHeader/AppHeader';

const documentEventsList = [
    { key: 1, label: "Sent", description: "Triggers when documents are sent for signatures or approval." },
    { key: 4, label: "Completed by all", description: "Triggers when documents are completely signed and approved by all recipients." },
    { key: 7, label: "Expires", description: "Triggers when documents sent for signatures or approval expire." }
];

const recipientEventsList = [
    { key: 2, label: "Viewed", description: "Triggers when documents are viewed by one of the recipients." },
    { key: 3, label: "Signed by recipient", description: "Triggers when documents are signed by one of the recipients." },
    { key: 6, label: "Reassigned", description: "Triggers when documents are reassigned by one recipient to another person." },
    { key: 5, label: "Declined", description: "Triggers when documents are declined by one of the recipients." },
    { key: 8, label: "Recalled", description: "Triggers when documents sent for signatures or approval are recalled by the sender." }
];


const WebhooksPage = () => {
    const [webhooks, setWebhooks] = useState([]);

    const [anchorEl, setAnchorEl] = useState(null);
    const [selectedIndex, setSelectedIndex] = useState(null);
    const [showForm, setShowForm] = useState(false);
    const [newWebhook, setNewWebhook] = useState({
        Webhook_URL: "",
        Purpose: "",
        status: true,
        Subscribed_Events: []
    });
    const [searchTerm, setSearchTerm] = useState("");
    const [pageIndex, setPageIndex] = useState(0);
    const [pageSize, setPageSize] = useState(10);
    const [checkedIds, setCheckedIds] = useState([]);

    const open = Boolean(anchorEl);

    const dispatch = useDispatch();
    const { OrgId } = useSelector((state) => state?.document?.value);

    useEffect(() => {
        if (webhooks?.length) return;
        handleGetWebhook();
    }, []);

    const handleGetWebhook = () => {
        dispatch(toggleLoader(true));
        axios.get(process.env.REACT_APP_WEBHOOK, { params: { org_id: OrgId }, withCredentials: process.env.REACT_APP_CREDENTIALS })
            .then((res) => {
                const modifiedResponse = res?.data?.data?.map(item => ({
                    ...item,
                    Subscribed_Events: item.Subscribed_Events
                        ? item.Subscribed_Events.split(",").map(Number)
                        : []
                }));

                setWebhooks(modifiedResponse);
                dispatch(toggleLoader(false));
            })
            .catch((err) => {
                toast.error(err.message);
                dispatch(toggleLoader(false));
            });
    };

    // Filter webhooks based on search term
    const filteredWebhooks = webhooks.filter(webhook => {
        if (!searchTerm) return true;
        const searchLower = searchTerm.toLowerCase();
        return (
            webhook.Webhook_URL?.toLowerCase().includes(searchLower) ||
            webhook.Purpose?.toLowerCase().includes(searchLower)
        );
    });

    // Calculate pagination values
    const pageSizeArr = [10, 20, 30, 40, 50];
    const totalRecordCount = filteredWebhooks.length;
    const sliceFrom = pageIndex * pageSize + 1;
    const sliceTo = Math.min((pageIndex + 1) * pageSize, totalRecordCount);

    // Get paginated webhooks
    const paginatedWebhooks = filteredWebhooks.slice(
        pageIndex * pageSize,
        (pageIndex + 1) * pageSize
    );

    // Handle page navigation
    const handlePageHandler = (type) => {
        if (type === 'increment') {
            setPageIndex(prevIndex => prevIndex + 1);
        } else if (type === 'decrement') {
            setPageIndex(prevIndex => prevIndex - 1);
        }
    };

    // Handle search
    const handleSearch = (e) => {
        setSearchTerm(e.target.value);
        setPageIndex(0); // Reset to first page when searching
        setCheckedIds([]); // Clear selections
    };

    // Handle page size change
    const handlePageSizeChange = (e) => {
        setPageSize(e.target.value);
        setPageIndex(0); // Reset to first page
        setCheckedIds([]); // Clear selections
    };

    const handleMenuClick = (event, webhookId) => {
        setAnchorEl(event.currentTarget);
        setSelectedIndex(webhookId);
    };

    const handleMenuClose = () => {
        setAnchorEl(null);
    };

    const handleEdit = () => {
        const webhook = webhooks.find(w => w.Id === selectedIndex);
        if (webhook) {
            setNewWebhook(webhook);
            handleMenuClose();
            setShowForm(true);
        }
    };

    const handleDelete = () => {
        let data = { org_id: OrgId, id: selectedIndex };
        dispatch(toggleLoader(true));
        axios.delete(process.env.REACT_APP_WEBHOOK, { withCredentials: process.env.REACT_APP_CREDENTIALS, data: data })
            .then((res) => {
                setWebhooks(webhooks.filter(w => w.Id !== selectedIndex));
                handleMenuClose();
                dispatch(toggleLoader(false));
                toast.success("Webhook deleted successfully");
            })
            .catch((err) => {
                toast.error(err.message);
                dispatch(toggleLoader(false));
            });
    };

    const handleBulkDelete = async () => {
        if (checkedIds.length === 0) return;

        dispatch(toggleLoader(true));
        try {
            // Delete each selected webhook
            for (const id of checkedIds) {
                await axios.delete(process.env.REACT_APP_WEBHOOK, {
                    withCredentials: process.env.REACT_APP_CREDENTIALS,
                    data: { org_id: OrgId, id: id }
                });
            }

            // Update the webhooks list
            setWebhooks(webhooks.filter(w => !checkedIds.includes(w.Id)));
            setCheckedIds([]);
            toast.success(`${checkedIds.length} webhook(s) deleted successfully`);
        } catch (err) {
            toast.error(err.message || "Failed to delete webhooks");
        } finally {
            dispatch(toggleLoader(false));
        }
    };

    const handleToggle = async (webhookId) => {
        const webhook = webhooks.find(w => w.Id === webhookId);
        if (!webhook) return;

        const newStatus = webhook.Is_Active ? 0 : 1;

        try {
            await axios.put(process.env.REACT_APP_WEBHOOK, {
                org_id: OrgId,
                id: webhook.Id,
                is_active: newStatus,
            }, {
                withCredentials: process.env.REACT_APP_CREDENTIALS
            });

            // Update the webhook in the list
            const updated = webhooks.map(w =>
                w.Id === webhookId ? { ...w, Is_Active: Boolean(newStatus) } : w
            );
            setWebhooks(updated);
            toast.success("Status updated successfully");
        } catch (err) {
            toast.error(err.message || "Failed to update status");
        }
    };

    const handleEventChange = (event) => {
        const eventKey = Number(event);
        setNewWebhook((prev) => {
            const alreadySelected = prev.Subscribed_Events.includes(eventKey);
            return {
                ...prev,
                Subscribed_Events: alreadySelected
                    ? prev.Subscribed_Events.filter((e) => e !== eventKey)
                    : [...prev.Subscribed_Events, eventKey]
            };
        });
    };

    const handleFormSubmit = async (e) => {
        e.preventDefault();

        const formData = {
            org_id: OrgId,
            webhook_url: newWebhook?.Webhook_URL,
            subscribed_events: newWebhook?.Subscribed_Events || [],
            purpose: newWebhook?.Purpose,
        };

        const isUpdating = Boolean(newWebhook?.Id);
        if (isUpdating) {
            Object.assign(formData, {
                id: newWebhook?.Id,
                is_active: newWebhook?.Is_Active,
                secret_token: newWebhook?.secret_token,
            });
        }

        dispatch(toggleLoader(true));

        try {
            const method = isUpdating ? axios.put : axios.post;
            await method(process.env.REACT_APP_WEBHOOK, formData, {
                withCredentials: process.env.REACT_APP_CREDENTIALS,
            });

            handleGetWebhook();
            setWebhooks([...webhooks, newWebhook]);
            setNewWebhook({
                Webhook_URL: "",
                Purpose: "",
                status: true,
                Subscribed_Events: [],
            });
            setShowForm(false);
        } catch (err) {
            toast.error(err.response.data.error.details ? err.response.data.error.details : err.message || "Something went wrong");
        } finally {
            dispatch(toggleLoader(false));
        }
    };


    if (showForm) {
        return (
            <Box sx={{ padding: '0px 30px 30px', overflow: "auto", height: "calc(100vh - 120px)" }}>
                <Box display="flex" alignItems="center" mb={2} pt={2}>
                    <IconButton onClick={() => setShowForm(false)} sx={{ mr: 1 }}>
                        <ArrowBackIosNewIcon sx={{ fontSize: 15 }} />
                    </IconButton>
                    <Typography variant="h6" sx={{ fontWeight: "bold", color: "var(--header-color)", fontFamily: "var(--primary-font-family)" }}>
                        Create Webhook
                    </Typography>
                </Box>

                <form onSubmit={handleFormSubmit}>
                    <Box pl={5}>
                        <Box sx={{ display: "flex", mb: 1, width: "80%" }}>
                            <Typography
                                sx={{ width: 200 }}
                                fontFamily="var(--primary-font-family)"
                                fontSize="14px"
                                color="var(--table-font-color)"
                            >
                                Callback URL
                            </Typography>
                            <TextField
                                fullWidth
                                value={newWebhook.Webhook_URL}
                                placeholder="http://example.com"
                                onChange={(e) =>
                                    setNewWebhook({ ...newWebhook, Webhook_URL: e.target.value })
                                }
                                sx={{
                                    '& fieldset': { border: "1px solid var(--border-color)" },
                                    '& .MuiOutlinedInput-root': {
                                        '&:hover fieldset': {
                                            borderColor: 'var(--primary)',
                                        },
                                        fontSize: '14px',
                                        paddingLeft: '2px',
                                    },
                                }}
                                name="Webhook_URL"
                                variant="outlined"
                                type="text"
                            />
                        </Box>

                        <Box sx={{ display: "flex", mb: 1, width: "80%" }}>
                            <Typography
                                sx={{ width: 200 }}
                                fontFamily="var(--primary-font-family)"
                                fontSize="14px"
                                color="var(--table-font-color)"
                            >
                                Name
                            </Typography>
                            <TextField
                                fullWidth
                                value={newWebhook.Purpose}
                                placeholder="Enter webhook name"
                                onChange={(e) =>
                                    setNewWebhook({ ...newWebhook, Purpose: e.target.value })
                                }
                                sx={{
                                    '& fieldset': { border: "1px solid var(--border-color)" },
                                    '& .MuiOutlinedInput-root': {
                                        '&:hover fieldset': {
                                            borderColor: 'var(--primary)',
                                        },
                                        fontSize: '14px',
                                        paddingLeft: '2px',
                                    },
                                }}
                                name="name"
                                variant="outlined"
                                type="text"
                            />
                        </Box>
                        <Box mt={3} mb={2}>
                            <Typography variant="h6" sx={{ fontWeight: "bold", color: "var(--header-color)", fontFamily: "var(--primary-font-family)" }}>
                                Callback events
                            </Typography>
                            <Typography mt={1} mb={2} color="var(--table-font-color)" fontFamily="var(--primary-font-family)" fontSize="14px">Document Events</Typography>
                            <FormGroup>
                                {documentEventsList.map((ev) => (
                                    <Box key={ev.key} sx={{ display: "flex", alignItems: "flex-start", my: 0.5 }}>
                                        <Checkbox
                                            checked={newWebhook?.Subscribed_Events?.includes(ev.key)}
                                            onChange={(e) => handleEventChange(ev.key, e)}
                                            sx={{ transform: "scale(0.8)", padding: "4px" }}
                                        />
                                        <Box sx={{ display: "flex", flexDirection: "column" }}>
                                            <Typography sx={{ fontSize: 14, fontWeight: 500 }}>{ev.label}</Typography>
                                            {ev.description && (
                                                <Typography sx={{ fontSize: 12, color: "text.secondary", lineHeight: 1.4 }}>
                                                    {ev.description}
                                                </Typography>
                                            )}
                                        </Box>
                                    </Box>
                                ))}
                            </FormGroup>

                        </Box>

                        {/* <Divider sx={{ my: 2 }} /> */}

                        <Box mb={2} mt={5}>
                            <Typography mb={2} color="var(--table-font-color)" fontFamily="var(--primary-font-family)" fontSize="14px">Recipient Events</Typography>
                            <FormGroup>
                                {recipientEventsList.map((ev) => (
                                    <Box key={ev.key} sx={{ display: "flex", alignItems: "flex-start", my: 0.5 }}>
                                        <Checkbox
                                            checked={newWebhook?.Subscribed_Events?.includes(ev.key)}
                                            onChange={(e) => handleEventChange(ev.key, e)}
                                            sx={{ transform: "scale(0.8)", padding: "4px" }}
                                        />
                                        <Box sx={{ display: "flex", flexDirection: "column" }}>
                                            <Typography sx={{ fontSize: 14, fontWeight: 500 }}>{ev.label}</Typography>
                                            {ev.description && (
                                                <Typography sx={{ fontSize: 12, color: "text.secondary", lineHeight: 1.4 }}>
                                                    {ev.description}
                                                </Typography>
                                            )}
                                        </Box>
                                    </Box>
                                ))}
                            </FormGroup>

                        </Box>

                        <Box display="flex" gap={2} mt={3}>
                            <Button type="submit" variant="contained" sx={{ textTransform: "capitalize" }} disabled={!newWebhook?.Webhook_URL || !newWebhook?.Purpose}>Save</Button>
                            <Button variant="outlined" onClick={() => setShowForm(false)} sx={{ textTransform: "capitalize" }}>Cancel</Button>
                        </Box>
                    </Box>
                </form>
            </Box>
        );
    };

    return (
        <Box sx={{padding: '0px 30px 30px', overflow: "visible", height: "auto" }}>
            <AppHeader
                moduleText='Webhooks'
                btnText='Create webhook'
                onBtnClick={() => {
                    setShowForm(true);
                    setNewWebhook({
                        Webhook_URL: "",
                        Purpose: "",
                        status: true,
                        Subscribed_Events: [1, 4, 5],
                    })
                }}
                rolePermissions={true}
                checkAdmin={true}
            />

            {/* Search and Pagination Controls */}
            <Box className={styles.controlsContainer}>
                <Box className={styles.nav_item} sx={{ width: '40%', gap: '15px' }}>
                    {/* Search Box */}
                    <Box className={styles.searchBox}>
                        <Box style={{ position: 'relative', zIndex: '1', marginLeft: '10px' }}>
                            <img
                                src={process.env.REACT_APP_IMAGE_URL + 'ToggleSvg/search.svg'}
                                width='12px'
                                height='12px'
                                alt='searchIcon'
                            />
                        </Box>
                        <TextField
                            placeholder="Search webhooks"
                            onChange={handleSearch}
                            value={searchTerm}
                            sx={{
                                maxWidth: '300px',
                                '& input': { fontSize: '13px', padding: '9px 10px 11px 33px' },
                                position: 'absolute',
                                "& input:hover": {
                                    backgroundColor: "#f6f7fa"
                                }
                            }}
                            className={styles.search_bar}
                            fullWidth
                            InputProps={{ disableUnderline: true }}
                            type="search"
                            variant='standard'
                        />
                    </Box>
                </Box>

                <Box className={styles.nav_item} sx={{ gap: '15px' }}>
                    {/* Delete Button */}
                    <Button
                        disabled={!checkedIds.length}
                        style={{
                            textTransform: 'unset',
                            cursor: !checkedIds.length ? 'not-allowed' : 'pointer',
                            borderColor: '#ed4c78',
                            color: '#ed4c78',
                            pointerEvents: 'auto',
                            fontFamily: 'var(--primary-font-family)'
                        }}
                        variant='outlined'
                        color='error'
                        startIcon={<DeleteIcon fontSize={'10px'} style={{ color: "#ed4c78" }} />}
                        onClick={handleBulkDelete}
                    >
                        Delete
                    </Button>

                    {/* Page Size Selector */}
                    <Select
                        MenuProps={{ classes: { paper: styles.select_pagination } }}
                        sx={{
                            "& .MuiOutlinedInput-notchedOutline": { border: 'none !important' },
                            m: '1px 0 1px 1px',
                            minWidth: 80,
                            width: '70px',
                            height: '38px',
                            border: '1px solid var(--border-color) !important',
                            fontFamily: 'var(--primary-font-family)',
                        }}
                        value={pageSize}
                        onChange={handlePageSizeChange}
                    >
                        {pageSizeArr.map((item, i) => (
                            <MenuItem className={styles.pagination_item} key={i} value={item}>
                                {item}
                            </MenuItem>
                        ))}
                    </Select>

                    {/* Total Record Count */}
                    <Box sx={{ pr: '10px', fontFamily: 'var(--primary-font-family)' }}>
                        of {totalRecordCount ?? 0}
                    </Box>

                    {/* Current Page Range */}
                    <Typography style={{ whiteSpace: 'nowrap', fontFamily: 'var(--primary-font-family)' }}>
                        {totalRecordCount > 0 ? `${sliceFrom} - ${sliceTo}` : '0 - 0'}
                    </Typography>

                    {/* Previous/Next Buttons */}
                    <Box style={{ display: 'flex', gap: '5px' }}>
                        <IconButton
                            onClick={() => handlePageHandler('decrement')}
                            sx={{ padding: '4px !important' }}
                            disabled={pageIndex < 1 || pageSize > totalRecordCount}
                        >
                            <ArrowBackIosIcon className={styles.arrow_icons} />
                        </IconButton>
                        <IconButton
                            onClick={() => handlePageHandler('increment')}
                            sx={{ padding: '4px !important' }}
                            disabled={totalRecordCount < pageIndex * pageSize + pageSize + 1}
                        >
                            <ArrowForwardIosIcon className={styles.arrow_icons} />
                        </IconButton>
                    </Box>
                </Box>
            </Box>

            <Box sx={{
                border: "1px solid var(--border-color)",
                boxShadow: "rgba(140, 152, 164, 0.075) 0px 0.375rem 0.75rem",
                borderRadius: "12px",
                overflow: "hidden",
                mt: 2
            }}>
                <Box
                    className={`${styles.tableContainer} ${filteredWebhooks.length === 0 ? styles.tableContainerNoData : ''}`}
                >
                    {filteredWebhooks.length === 0 && (
                        <Box className={styles.noDataFound}>
                            No data found
                        </Box>
                    )}

                    <TableContainer
                        component={Paper}
                        sx={{
                            boxShadow: "none",
                            borderRadius: 0
                        }}
                    >
                    <Table size="small" sx={{ minWidth: 650, border: "1.5px solid rgba(231, 234, 243, 0.7)" }}>
                        <TableHead>
                            <TableRow sx={{ backgroundColor: "#fafafd" }}>
                                <TableCell sx={{
                                    fontWeight: 400,
                                    fontSize: "12px",
                                    color: "var(--table-header-font)",
                                    background: "#f8fafd",
                                    textTransform: "uppercase",
                                    borderBottom: "1px solid var(--border-color)",
                                    fontFamily: "var(--primary-font-family)",
                                    width: "50px"
                                }}>
                                    <Checkbox
                                        checked={checkedIds.length === paginatedWebhooks.length && paginatedWebhooks.length > 0}
                                        indeterminate={checkedIds.length > 0 && checkedIds.length < paginatedWebhooks.length}
                                        onChange={(e) => {
                                            if (e.target.checked) {
                                                setCheckedIds(paginatedWebhooks.map(w => w.Id));
                                            } else {
                                                setCheckedIds([]);
                                            }
                                        }}
                                        sx={{ transform: "scale(0.8)" }}
                                    />
                                </TableCell>
                                <TableCell sx={{
                                    fontWeight: 400,
                                    fontSize: "12px",
                                    color: "var(--table-header-font)",
                                    background: "#f8fafd",
                                    textTransform: "uppercase",
                                    borderBottom: "1px solid var(--border-color)",
                                    fontFamily: "var(--primary-font-family)"
                                }}>URL</TableCell>
                                <TableCell sx={{
                                    fontWeight: 400,
                                    fontSize: "12px",
                                    color: "var(--table-header-font)",
                                    background: "#f8fafd",
                                    textTransform: "uppercase",
                                    borderBottom: "1px solid var(--border-color)",
                                    fontFamily: "var(--primary-font-family)"
                                }}>PURPOSE</TableCell>
                                <TableCell sx={{
                                    fontWeight: 400,
                                    fontSize: "12px",
                                    color: "var(--table-header-font)",
                                    background: "#f8fafd",
                                    textTransform: "uppercase",
                                    borderBottom: "1px solid var(--border-color)",
                                    fontFamily: "var(--primary-font-family)"
                                }}>STATUS</TableCell>
                                <TableCell sx={{
                                    fontWeight: 400,
                                    fontSize: "12px",
                                    color: "var(--table-header-font)",
                                    background: "#f8fafd",
                                    textTransform: "uppercase",
                                    borderBottom: "1px solid var(--border-color)",
                                    fontFamily: "var(--primary-font-family)"
                                }}>ACTIONS</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {paginatedWebhooks.map((row, index) => (
                                <TableRow key={row.Id || index} sx={{ backgroundColor: "#fff" }}>
                                    <TableCell sx={{ border: "unset", width: "50px" }}>
                                        <Checkbox
                                            checked={checkedIds.includes(row.Id)}
                                            onChange={(e) => {
                                                if (e.target.checked) {
                                                    setCheckedIds([...checkedIds, row.Id]);
                                                } else {
                                                    setCheckedIds(checkedIds.filter(id => id !== row.Id));
                                                }
                                            }}
                                            sx={{ transform: "scale(0.8)" }}
                                        />
                                    </TableCell>
                                    <TableCell sx={{
                                        border: "unset",
                                        fontFamily: "var(--primary-font-family)",
                                        fontSize: "14px",
                                        color: "var(--table-font-color)"
                                    }}>{row.Webhook_URL}</TableCell>
                                    <TableCell sx={{
                                        border: "unset",
                                        fontFamily: "var(--primary-font-family)",
                                        fontSize: "14px",
                                        color: "var(--table-font-color)"
                                    }}>{row.Purpose}</TableCell>
                                    <TableCell sx={{ border: "unset" }}>
                                        <Switch
                                            checked={row.Is_Active === 1 ? true : false}
                                            onChange={() => handleToggle(row.Id)}
                                        />
                                    </TableCell>
                                    <TableCell sx={{ border: "unset" }}>
                                        <IconButton onClick={(e) => handleMenuClick(e, row.Id)}>
                                            <MoreVertIcon />
                                        </IconButton>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
                </Box>
            </Box>

            <Menu anchorEl={anchorEl} open={open} onClose={handleMenuClose}>
                <MenuItem onClick={handleEdit} sx={{ fontSize: "14px" }}>Edit</MenuItem>
                <MenuItem onClick={handleDelete} sx={{ fontSize: "14px" }}>Delete</MenuItem>
            </Menu>
        </Box>
    );
};

export default WebhooksPage;
